import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ArrowDown, ArrowUp, ChevronLeft, ChevronRight, Search, X } from 'lucide-react';
import { CustomButton } from '@/components/molecules/CustomButton';
import { Loader } from '@/components/atoms/Loader';
import { getOwnedPokemons, type OwnedPokemon } from '@/services/pokemon';
import { cdnUrl } from '@/utils/cdn';
import { TYPE_COLORS } from '@/utils/pokemonTypes';

const SORT_OPTIONS: { value: 'createdAt' | 'level' | 'nickname'; label: string }[] = [
  { value: 'level', label: 'Niveau' },
  { value: 'nickname', label: 'Nom' },
  { value: 'createdAt', label: 'Obtention' },
];

const LIMIT_OPTIONS = [25, 50, 100] as const;

interface PokemonPickerProps {
  /** Appelé au clic sur un Pokémon non-KO et sans loading */
  onSelect: (pokemon: OwnedPokemon) => void;
  /** ownedPokemonId du Pokémon actuellement sélectionné */
  selectedId?: string | null;
  /** Si true, désactive toutes les interactions (pendant un appel API) */
  loading?: boolean;
  /** Clé unique pour le cache React Query — évite les conflits entre instances */
  queryKey?: string;
  /** Retourne un label de badge si le Pokémon est non éligible, null sinon */
  getOverlay?: (pokemon: OwnedPokemon) => string | null;
}

export const PokemonPicker = ({
  onSelect,
  selectedId,
  loading = false,
  queryKey = 'picker',
  getOverlay,
}: PokemonPickerProps) => {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [offset, setOffset] = useState(0);
  const [sortBy, setSortBy] = useState<'createdAt' | 'level' | 'nickname'>('level');
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC');
  const [limit, setLimit] = useState<25 | 50 | 100>(25);
  const [onlyShiny, setOnlyShiny] = useState(false);
  const [onlyLegendary, setOnlyLegendary] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
      setOffset(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: pokemonsData, isLoading } = useQuery({
    queryKey: [queryKey, offset, limit, sortBy, sortOrder, debouncedSearch, onlyShiny, onlyLegendary],
    queryFn: () =>
      getOwnedPokemons({
        offset,
        limit,
        sortBy,
        sortOrder,
        search: debouncedSearch || undefined,
        onlyShiny,
        onlyLegendary,
      }),
    placeholderData: keepPreviousData,
  });

  const total = pokemonsData?.total ?? 0;
  const pokemons = pokemonsData?.data ?? [];
  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  const resetOffset = () => setOffset(0);

  return (
    <div className={loading ? 'pointer-events-none opacity-60' : undefined}>
      <div className="mb-3 flex gap-2">
        <form
          role="search"
          className="relative min-w-0 flex-1"
          onSubmit={(e) => e.preventDefault()}
        >
          <Search
            size={14}
            className="text-content/40 pointer-events-none absolute top-1/2 left-3 -translate-y-1/2"
          />
          <input
            type="text"
            aria-label="Rechercher un Pokémon"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Rechercher..."
            className="text-content placeholder:text-content/40 focus:ring-primary-400 h-9 w-full rounded-xl border border-line-soft bg-raised pr-7 pl-8 text-sm focus:ring-2 focus:outline-none"
          />
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              className="text-content/40 hover:text-content/70 absolute top-1/2 right-2.5 -translate-y-1/2 transition-colors"
              aria-label="Effacer la recherche"
            >
              <X size={13} />
            </button>
          )}
        </form>

        <select
          value={sortBy}
          onChange={(e) => {
            setSortBy(e.target.value as typeof sortBy);
            resetOffset();
          }}
          className="text-content h-9 rounded-xl border border-line-soft bg-raised px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400"
        >
          {SORT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => {
            setSortOrder((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
            resetOffset();
          }}
          className="text-content/60 hover:text-content flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-line-soft bg-raised transition-colors hover:bg-line-soft"
          title={sortOrder === 'ASC' ? 'Croissant' : 'Décroissant'}
        >
          {sortOrder === 'ASC' ? <ArrowUp size={14} /> : <ArrowDown size={14} />}
        </button>
        <button
          type="button"
          onClick={() => { setOnlyShiny(!onlyShiny); resetOffset(); }}
          className={`h-9 rounded-xl px-2.5 text-sm font-semibold transition-colors ${
            onlyShiny
              ? 'bg-yellow-400 text-white'
              : 'border border-line-soft bg-raised text-content/60 hover:bg-line-soft'
          }`}
          title="Shiny uniquement"
        >
          ✨
        </button>
        <button
          type="button"
          onClick={() => { setOnlyLegendary(!onlyLegendary); resetOffset(); }}
          className={`h-9 rounded-xl px-2.5 text-sm font-semibold transition-colors ${
            onlyLegendary
              ? 'bg-amber-500 text-white'
              : 'border border-line-soft bg-raised text-content/60 hover:bg-line-soft'
          }`}
          title="Légendaires uniquement"
        >
          🟨
        </button>
      </div>

      <p className="text-content/50 mb-3 text-xs">
        {pokemonsData
          ? `${total} Pokémon${total !== 1 ? 's' : ''} disponible${total !== 1 ? 's' : ''}`
          : '\u00A0'}
      </p>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader size={36} color="#f29602" />
        </div>
      ) : pokemons.length === 0 ? (
        <div className="flex flex-col items-center gap-3 py-10">
          <p className="text-5xl">🔍</p>
          <p className="text-content/50 text-sm">
            {debouncedSearch
              ? `Aucun Pokémon trouvé pour «\u00A0${debouncedSearch}\u00A0»`
              : 'Aucun Pokémon disponible'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {pokemons.map((ownedPokemon) => {
            if (!ownedPokemon.pokemon) return null;
            const isKO = ownedPokemon.hp === 0;
            const overlay = getOverlay?.(ownedPokemon) ?? null;
            const isDisabled = isKO || overlay !== null;
            const isSelected = selectedId === ownedPokemon.ownedPokemonId;
            return (
              <button
                key={ownedPokemon.ownedPokemonId}
                onClick={() => !isDisabled && onSelect(ownedPokemon)}
                disabled={isDisabled}
                title={isKO ? "Ce Pokémon n'a plus de PV" : overlay ?? undefined}
                className={`relative flex flex-col items-center rounded-2xl border p-3 text-center transition-all duration-200 ${
                  isDisabled
                    ? 'cursor-not-allowed border-transparent bg-raised opacity-50'
                    : isSelected
                      ? 'border-primary-400 bg-primary-500/10 shadow-md'
                      : 'hover:border-primary-200 border-transparent bg-surface shadow-sm hover:shadow-md'
                }`}
              >
                {ownedPokemon.isShiny && !isDisabled && (
                  <span className="bg-primary-500 absolute top-2 right-2 rounded-full px-1.5 py-0.5 text-[10px] font-bold leading-none text-white">
                    ★
                  </span>
                )}
                {isKO && (
                  <span className="absolute top-2 right-2 rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-red-500">
                    KO
                  </span>
                )}
                {overlay && (
                  <span className="absolute top-2 left-2 rounded bg-gray-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                    {overlay}
                  </span>
                )}
                <div className={`mb-2 flex h-16 w-16 items-center justify-center rounded-xl ${isKO ? 'bg-raised' : 'bg-primary-500/10'}`}>
                  <img
                    src={cdnUrl(ownedPokemon.isShiny ? ownedPokemon.pokemon.spriteShiny : ownedPokemon.pokemon.sprite)}
                    alt={ownedPokemon.nickname}
                    loading="lazy"
                    className={`h-14 w-14 object-contain ${isKO ? 'grayscale' : ''}`}
                    onError={(e) => { e.currentTarget.style.opacity = '0'; }}
                  />
                </div>
                <p className="font-lexend text-content w-full truncate text-sm font-semibold">
                  {ownedPokemon.nickname}
                </p>
                <p className="text-content/50 mb-1.5 text-xs">{ownedPokemon.pokemon.realname}</p>
                <div className="mb-1.5 flex flex-wrap justify-center gap-1">
                  {ownedPokemon.pokemon.types.map((typeName) => (
                    <span
                      key={typeName}
                      className={`${TYPE_COLORS[typeName] ?? 'bg-gray-400'} rounded px-1.5 py-0.5 text-[10px] font-semibold text-white capitalize`}
                    >
                      {typeName}
                    </span>
                  ))}
                </div>
                <span className="bg-secondary-500 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white">
                  Nv.&nbsp;{ownedPokemon.level}
                </span>
              </button>
            );
          })}
        </div>
      )}

      {total > 0 && (
        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <CustomButton
              icon={<ChevronLeft size={16} />}
              iconOnly
              color="white"
              rounded="full"
              disabled={offset === 0}
              onClick={() => setOffset(Math.max(0, offset - limit))}
            />
            {totalPages > 1 && (
              <span className="text-content font-lexend text-sm font-semibold tabular-nums">
                {currentPage}&nbsp;/&nbsp;{totalPages}
              </span>
            )}
            <CustomButton
              icon={<ChevronRight size={16} />}
              iconOnly
              color="white"
              rounded="full"
              disabled={offset + limit >= total}
              onClick={() => setOffset(offset + limit)}
            />
          </div>

          <div className="flex gap-1">
            {LIMIT_OPTIONS.map((pageSize) => (
              <button
                key={pageSize}
                type="button"
                onClick={() => { setLimit(pageSize); resetOffset(); }}
                className={`h-8 rounded-lg px-2.5 text-xs font-semibold transition-colors ${
                  limit === pageSize
                    ? 'bg-primary-500 text-white'
                    : 'text-content/60 border border-line-soft bg-raised hover:bg-line-soft'
                }`}
              >
                {pageSize}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
